import React, { createContext, useEffect, useState, useMemo } from "react";
import axios from "axios";

import GradeSorter from "../utils/gradeSorter";
import AIStudioModal from "../components/AI/CGFAIStudioModal";
import useCGFAI from "../hooks/useCGFAI";

export const GlobalContext = createContext();

export const GlobalProvider = (props) => {
  const [userDetails, setUserDetails] = useState({
    isAuthenticated: false,
    name: "",
    userId: "",
    schoolId: "",
    role: "",
    profileImageUrl: undefined,
    selectedTab: "Dashboard",
    pathName: {
      subjectName: "",
      chapterName: "",
      topicName: "",
    },
    questions: [],
    grades: [],
    filterquestions: [],
  });
  // const [selectedTab, setSelectedTab] = useState("Dashboard");
  const cgfAIStudio = useCGFAI();
  const [loading, setLoading] = useState(false);
  const [lang, setLanguage] = useState("english");
  const [eligible, setEligibility] = useState(false);
  const [assignedGrades, setAssignedGrades] = useState({
    loading: true,
    grades: [],
  });
  const [studentLoading, setStudentLoading] = useState(false);
  const [students, setStudents] = useState({}); //{[sectionName]:students[]}
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [sectionIdTask, setSectionIdTask] = useState("");
  const [sectionId, setSectionId] = useState("");

  const boardLevelAccess = useMemo(
    () => ["cbse"].includes((userDetails?.schoolId?.board || "").toLowerCase()),
    [userDetails],
  );
  const accountLevelAccess = useMemo(
    () =>
      [
        "62f4a20d19c96e484f0ca743",
        "611c6ff03deec731e4c4037d",
        "64958456bc10bb27202c6271",
        "61239f24ffd0dc00162fbb02"
      ].includes(userDetails?.userId),
    [userDetails],
  );
  const cbqBetaAccess = useMemo(() => {
    return ["62f4a20d19c96e484f0ca743","611c6ff03deec731e4c4037d"].includes(userDetails?.userId);
  },[userDetails]);
  const haveBetaAccess = useMemo(() => {
    return accountLevelAccess || boardLevelAccess;
  }, [userDetails]);

  async function getStudents(gradeSection) {
    if (studentLoading) return;
    setStudentLoading(true);
    const authToken = localStorage.getItem("authToken");
    try {
      const grade = gradeSection.split("-")[0];
      const section = gradeSection.split("-")[1];
      const response = await axios.get(
        `${process.env.REACT_APP_ORIGIN}/api/v1/grades/${grade}/sections/${section}/students`,
        {
          headers: {
            token: authToken,
          },
        },
      );

      setStudentLoading(false);
      if (response.status === 200) {
        setStudents({
          ...students,
          [gradeSection]: response.data.data.students,
        });
        return response.data.data.students;
      }
    } catch (error) {
      setStudentLoading(false);
      console.log(error);
      return [];
    }
    setStudentLoading(false);
  }

  const selectGrade = (gradeName, sectionId) => {
    setSectionId(sectionId);
    localStorage.setItem("currentsectionId", sectionId);
    localStorage.setItem("gradeSection", gradeName);
    setSelectedClass(gradeName);
  };

  const subjectIds = useMemo(() => {
    if (assignedGrades.loading) return [];
    return assignedGrades.grades
      .map((grade) =>
        grade.subjects.map((sub) => {
          return {
            sectionId: sub.sectionId,
            gradeSection: grade._id,
            mainSubjectId: sub.subjectData?._id,
          };
        }),
      )
      .flat(1)
      .reduce((prevValue, currentValue) => {
        prevValue[currentValue.mainSubjectId] = currentValue;
        return prevValue;
      }, {});
  }, [assignedGrades]);

  useEffect(() => {
    const checkAuth = async () => {
      const authToken = localStorage.getItem("authToken");

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ORIGIN}/api/v1/auth/validate/${authToken}`,
        );
        if (response.data.status === "authorized") {
          setUserDetails({
            isAuthenticated: true,
            name: response.data.data.user.name,
            userId: response.data.data.user.userId,
            schoolId: response.data.data.user.school,
            role: response.data.data.user.role,
            grades: response.data.data.user.grades,
            sections: response.data.data.user.sections,
            selectedTab: "Dashboard",
            pathName: userDetails.pathName,
            profileImageUrl: window.localStorage.getItem("profileImageUrl"),
            questions: userDetails.questions,
            filterquestions: userDetails.filterquestions,
          });
        }

        setLoading(true);
      } catch (error) {
        setUserDetails({ isAuthenticated: false });
        setLoading(true);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!userDetails.isAuthenticated || assignedGrades.grades.length) return;
    const fetchClasses = async () => {
      const authToken = localStorage.getItem("authToken");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ORIGIN}/api/v1/staffs/${userDetails.userId}/assignSubjects`,
          {
            headers: {
              token: authToken,
            },
          },
        );

        if (response.data.status === "success") {
          setAssignedGrades({
            loading: false,
            grades: new GradeSorter(response.data.data.sections).sort(),
          });
          //set elgibility
          if (
            response.data.data.schoolId._id === "611ce503ded7800500d3b287" ||
            (response.data.data.schoolId?.board || "").toLowerCase() === "cbse"
          ) {
            setEligibility(true);
          }
          if (response.data.data?.schoolId?.board.startsWith("Chhattisgarh")) {
            setLanguage("hindi");
          }
        }
      } catch (error) {
        setAssignedGrades({
          loading: false,
          grades: [],
        });
        console.log(error);
      }
    };
    fetchClasses();
  }, [userDetails]);

  useEffect(() => {
    if (assignedGrades.grades.length) {
      const query = new URLSearchParams(window.location.search);
      const gradeName = query.get("gradeName");
      const grade = gradeName
        ? (assignedGrades?.grades || []).find(
            (grade) => grade._id === gradeName,
          )
        : assignedGrades.grades[0];
      if (!grade) return;
      const sectionId = grade.subjects.length && grade?.subjects[0]?.sectionId;
      selectGrade(grade._id, sectionId);
    }
  }, [assignedGrades]);

  const cgfSchoolQuery = useMemo(() => {
    if (!userDetails.isAuthenticated || !cgfAIStudio.token) return;
    return {
      teacherId: userDetails.userId,
      schoolId: userDetails.schoolId?._id || userDetails.schoolId,
    }
  }, [cgfAIStudio, userDetails]);

  return (
    <>
      {loading && (
        <GlobalContext.Provider
          value={{
            userDetails,
            setUserDetails,
            lang,
            setLanguage,
            eligible,
            subjectIds,
            assignedGrades,
            setAssignedGrades,
            assignedSubjects,
            setAssignedSubjects,
            selectedClass,
            setSelectedClass,
            sectionIdTask,
            setSectionIdTask,
            sectionId,
            setSectionId,
            students,
            getStudents,
            haveBetaAccess,
            cbqBetaAccess,
            accountLevelAccess,
            boardLevelAccess,
            cgfAIStudio,
          }}
        >
          {props.children}

          {accountLevelAccess ? (
            <div style={{ display: cgfAIStudio.isOpen ? "block" : "none" }}>
              <AIStudioModal {...cgfAIStudio} query={cgfSchoolQuery} />
            </div>
          ) : null}
        </GlobalContext.Provider>
      )}
    </>
  );
};
